import { useState } from "react";
import "./LegalMentions.css";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";

const LEGAL_URL = "/legal-mentions";

const LegalMentions = ({ onAccept, isLoading, error, onClose }) => {
  const [isCguChecked, setIsCguChecked] = useState(false);
  const [isDisclaimerChecked, setIsDisclaimerChecked] = useState(false);

  const openFullScreen = () => {
    window.open(LEGAL_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mentions-overlay">
      <div className="mentions-modal">
        {/* ── Header ── */}
        <div className="mentions-header">
          <div className="mentions-header-top">
            <div>
              <h2 className="mentions-title">CGU & Avertissement</h2>
              <p className="mentions-subtitle">
                Plateforme SecuFire Action — accès professionnel · Version du 30
                juin 2026
              </p>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexShrink: 0,
              }}
            >
              {onClose && (
                <button
                  className="mentions-close-btn"
                  onClick={onClose}
                  title="Fermer"
                >
                  <CloseIcon style={{ fontSize: "18px" }} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="mentions-body">
          {/* ══ CGU ══ */}
          <div className="mentions-chapter-title">
            Conditions Générales d'Utilisation
          </div>
          <p className="mentions-description">
            Vous devez lire et accepter ces conditions générales d'utilisation
            pour accéder à la plateforme. Cette acceptation sera enregistrée et
            horodatée.
          </p>
          <section className="mentions-section">
            <h3>Préambule</h3>
            <p>
              Les présentes Conditions Générales d'Utilisation (ci-après « CGU
              ») régissent l'accès et l'utilisation de la plateforme SecuFire
              Action (ci-après « la Plateforme »), mise à disposition par
              Climate Innov SAS en partenariat scientifique avec l'EDHEC Climate
              Institute, centre de recherche sur le climat de l'EDHEC Business
              School, (ci-après conjointement désignés « les Éditeurs »).
            </p>
            <p>
              L'accès à la Plateforme est strictement réservé aux professionnels
              habilités disposant d'un code d'accès personnel, dans le cadre
              d'une convention de partenariat préalablement signée avec les
              Éditeurs. Toute connexion à la Plateforme vaut acceptation pleine
              et entière des présentes CGU.
            </p>
          </section>

          <section className="mentions-section">
            <h3>Article 1 — Objet de la plateforme</h3>
            <p>
              SecuFire Action est une plateforme modulaire d'aide à la décision
              destinée aux acteurs publics de la prévention et de la lutte
              contre les feux de végétation. Elle met à disposition des
              utilisateurs habilités des données météorologiques, topographiques
              et de végétation, ainsi que des prédictions de propagation du feu
              fondées sur le modèle physique Balbi.
            </p>
            <p>
              La Plateforme constitue exclusivement un outil d'aide à la
              décision. Elle ne produit pas de décisions opérationnelles et ne
              se substitue en aucune circonstance au jugement professionnel des
              équipes de terrain, des commandants d'opérations de secours ou de
              toute autre autorité compétente.
            </p>
          </section>

          <section className="mentions-section">
            <h3>Article 2 — Conditions d'accès</h3>
            <p>
              <strong>2.1 Utilisateurs autorisés</strong>
            </p>
            <p>
              L'accès à la Plateforme est exclusivement réservé aux entités
              engagées dans la prévention et la lutte contre les feux de
              végétation, et notamment :
            </p>
            <ul className="mentions-list">
              <li>
                les services départementaux d'incendie et de secours (SDIS) ;
              </li>
              <li>
                les collectivités territoriales concernées par la gestion du
                risque incendie ;
              </li>
              <li>
                les gestionnaires forestiers publics (ONF et équivalents) ;
              </li>
              <li>
                les opérateurs publics d'infrastructures sensibles exposées au
                risque feu.
              </li>
            </ul>
            <p>
              L'accès est conditionné à la signature préalable d'une convention
              de partenariat avec l'EDHEC Climate Institute et Climate Innov
              SAS.
            </p>

            <p>
              <strong>2.2 Code d'accès personnel</strong>
            </p>
            <p>
              Chaque utilisateur habilité se voit attribuer un code d'accès
              personnel, strictement nominatif et non cessible. L'utilisateur
              s'engage à :
            </p>
            <ul className="mentions-list">
              <li>
                ne pas communiquer son code d'accès à un tiers, quel qu'il soit
                ;
              </li>
              <li>
                ne pas permettre l'accès à la Plateforme à toute personne non
                habilitée ;
              </li>
              <li>
                informer immédiatement les Éditeurs en cas de perte, vol ou
                usage non autorisé de son code d'accès.
              </li>
            </ul>
            <p>
              L'éditeur et son partenaire scientifique se réservent, ensemble ou
              séparément, le droit de suspendre ou révoquer tout code d'accès en
              cas de manquement aux présentes CGU, sans préavis ni indemnité.
            </p>
          </section>

          <section className="mentions-section">
            <h3>Article 3 — Obligations de l'utilisateur</h3>
            <p>
              L'utilisateur s'engage à utiliser la Plateforme conformément à sa
              destination et aux présentes CGU, et notamment à :
            </p>
            <ul className="mentions-list">
              <li>
                n'utiliser les données et prédictions produites par la
                Plateforme qu'à des fins professionnelles de prévention et de
                lutte contre les feux de végétation, dans le cadre de ses
                attributions ;
              </li>
              <li>
                ne pas reproduire, redistribuer, revendre ou publier les
                données, prédictions ou exports de la Plateforme à des tiers non
                habilités sans accord préalable écrit des Éditeurs ;
              </li>
              <li>
                ne pas tenter de contourner les mesures de sécurité ou
                d'authentification de la Plateforme ;
              </li>
              <li>
                ne pas utiliser la Plateforme à des fins commerciales ou
                concurrentielles ;
              </li>
              <li>
                partager avec les Éditeurs, dans les conditions prévues par la
                convention de partenariat, les retours d'expérience
                opérationnels permettant l'amélioration continue de l'outil.
              </li>
            </ul>
          </section>

          <section className="mentions-section">
            <h3>Article 4 — Disponibilité de la plateforme</h3>
            <p>
              Les Éditeurs s'efforcent d'assurer la disponibilité de la
              Plateforme 24h/24 et 7j/7. Ils se réservent toutefois le droit
              d'interrompre l'accès, sans préavis, pour des raisons de
              maintenance, de mise à jour, de sécurité ou de force majeure.
            </p>
            <p>
              Les Éditeurs ne sauraient être tenus responsables des
              interruptions de service, notamment en situation de crise, de
              surcharge exceptionnelle des serveurs, ou de défaillance des
              sources de données tierces (satellites, réseaux météorologiques,
              etc.).
            </p>
            <p>
              La Plateforme est fournie « en l'état », sans garantie de
              disponibilité continue ni d'exhaustivité des données à tout
              instant.
            </p>
          </section>

          <section className="mentions-section">
            <h3>Article 5 — Propriété des données</h3>
            <p>
              Les données produites par l'utilisateur dans le cadre de
              l'utilisation de la Plateforme (contours de feux, événements
              créés, exports) demeurent la propriété de l'utilisateur ou de la
              structure qu'il représente. L'utilisateur autorise toutefois les
              Éditeurs à utiliser ces données, dans le strict respect de
              l'anonymat et de la confidentialité, à des fins d'amélioration et
              d'entraînement des modèles scientifiques sous-jacents,
              conformément à la convention de partenariat.
            </p>
          </section>

          <section className="mentions-section">
            <h3>Article 6 — Limitation de responsabilité</h3>
            <p>
              L'utilisation des données, prédictions et informations fournies
              par SecuFire Action relève de la seule responsabilité de
              l'utilisateur. Les Éditeurs déclinent toute responsabilité pour :
            </p>
            <ul className="mentions-list">
              <li>
                les décisions opérationnelles prises sur la base des
                informations produites par la Plateforme ;
              </li>
              <li>
                les conséquences directes ou indirectes, matérielles ou
                immatérielles, résultant de l'utilisation ou de la
                non-utilisation des informations fournies ;
              </li>
              <li>
                les écarts entre les prédictions produites par la Plateforme et
                le comportement réel du feu ;
              </li>
              <li>
                toute interruption de service, perte de données ou dommage
                résultant d'une défaillance technique.
              </li>
            </ul>
            <div className="mentions-blockquote">
              <p>
                Les prédictions de propagation produites par SecuFire Action
                constituent des projections probabilistes fondées sur des
                modèles physiques et des données satellitaires. Elles ne
                sauraient être interprétées comme des certitudes
                opérationnelles.
              </p>
            </div>
          </section>

          <section className="mentions-section">
            <h3>Article 7 — Modification des CGU</h3>
            <p>
              Les Éditeurs se réservent le droit de modifier les présentes CGU à
              tout moment. Les utilisateurs seront informés de toute
              modification substantielle par voie de notification dans la
              Plateforme ou par email. La poursuite de l'utilisation de la
              Plateforme après notification vaut acceptation des nouvelles CGU.
            </p>
          </section>

          <section className="mentions-section">
            <h3>Article 8 — Résiliation</h3>
            <p>
              Les présentes CGU sont conclues pour la durée de la convention de
              partenariat liant l'utilisateur aux Éditeurs. En cas de
              résiliation de ladite convention, l'accès à la Plateforme est
              automatiquement suspendu. Les Éditeurs peuvent également résilier
              l'accès en cas de manquement grave aux présentes CGU, après mise
              en demeure restée sans effet pendant quinze jours.
            </p>
          </section>

          <section className="mentions-section">
            <h3>Article 9 — Droit applicable et juridiction compétente</h3>
            <p>
              Les présentes CGU sont soumises au droit français. En cas de
              litige relatif à leur interprétation ou à leur exécution, et à
              défaut de résolution amiable dans un délai de trente jours à
              compter de la notification du différend, les tribunaux compétents
              du ressort du siège de la SAS Climate Innov seront seuls
              compétents.
            </p>
          </section>

          <div className="mentions-note-box">
            <p>
              <strong>
                Menu déroulant à la première ouverture avec le mot de passe qui
                aura été fourni :
              </strong>{" "}
              l'utilisateur doit cocher en bas de menu déroulant pour accéder à
              l'outil à sa première connexion (et on conserve la preuve de
              l'acceptation)
            </p>
          </div>

          {/* ══ CHECKBOX D'ACCÈS AU DISCLAIMER ══ */}
          <div className="mentions-disclaimer-gate">
            <label className="mentions-checkbox-label">
              {/* <input
                type="checkbox"
                className="mentions-checkbox-input"
                checked={isDisclaimerChecked}
                onChange={(e) => setIsDisclaimerChecked(e.target.checked)}
              /> */}

              <input
                type="checkbox"
                className="mentions-checkbox-input"
                checked={isCguChecked}
                onChange={(e) => {
                  setIsCguChecked(e.target.checked);

                  // If the user unchecks it, also uncheck the disclaimer.
                  if (!e.target.checked) {
                    setIsDisclaimerChecked(false);
                  }
                }}
              />

              <span>
                J'ai pris connaissance des CGU ci-dessus et je souhaite
                consulter l'avertissement d'usage
              </span>
            </label>
          </div>

          {/* ══ DISCLAIMER ══ */}
          {isCguChecked && (
            <>
              <div className="mentions-chapter-title">
                Disclaimer — Avertissement d'usage
              </div>
              <p className="mentions-description">
                Vous devez lire et accepter cet avertissement pour accéder à la
                plateforme. Cette acceptation sera enregistrée et horodatée
              </p>
              <section className="mentions-section">
                <h3>1. Nature de l'outil</h3>
                <p>
                  SecuFire Action est un outil d'aide à la décision destiné aux
                  professionnels de la prévention et de la lutte contre les feux
                  de végétation. Il met à disposition des données
                  météorologiques, topographiques et de végétation, ainsi que
                  des modélisations de la propagation du feu.
                </p>
                <div className="mentions-blockquote">
                  <p>
                    SecuFire Action ne produit pas de décisions opérationnelles.
                    Il n'a pas vocation à se substituer au jugement
                    professionnel des équipes de terrain, des commandants
                    d'opérations de secours ou de toute autre autorité
                    compétente. La décision reste, en toutes circonstances,
                    celle des personnels habilités.
                  </p>
                </div>
              </section>

              <section className="mentions-section">
                <h3>2. Limites des prédictions</h3>
                <p>
                  Les prédictions de propagation du feu produites par SecuFire
                  Action sont fondées sur le modèle physique Balbi et sur des
                  données satellitaires de haute précision (Spire Global,
                  Sentinel-2, EUMETSAT, GFS, ERA5). Elles constituent des
                  projections probabilistes, calculées à partir des informations
                  disponibles au moment de la simulation.
                </p>
                <div className="mentions-blockquote">
                  <p>
                    Aucune prédiction ne peut garantir avec certitude le
                    comportement réel d'un incendie. Le feu peut évoluer
                    différemment des modélisations produites, notamment en
                    raison de variations météorologiques locales non captées par
                    les données d'entrée, de changements soudains de vent, de
                    caractéristiques du terrain ou de la végétation non
                    reflétées dans les couches disponibles, ou de tout autre
                    facteur imprévisible. Les prédictions ne doivent jamais être
                    utilisées comme seul critère de décision.
                  </p>
                </div>
              </section>

              <section className="mentions-section">
                <h3>3. Qualité et disponibilité des données</h3>
                <p>
                  La qualité des prédictions dépend directement de la qualité et
                  de la disponibilité des données d'entrée. En situation
                  opérationnelle de crise, certaines sources de données peuvent
                  être indisponibles, dégradées ou présentant des délais
                  d'actualisation. L'utilisateur est invité à tenir compte de
                  ces limitations dans l'interprétation des résultats.
                </p>
                <div className="mentions-blockquote">
                  <p>
                    Les Éditeurs ne garantissent pas la disponibilité continue
                    de la Plateforme ni l'exhaustivité des données à tout
                    instant. Toute interruption de service, notamment liée à une
                    défaillance de sources de données tierces ou à une surcharge
                    exceptionnelle, ne saurait engager leur responsabilité.
                  </p>
                </div>
              </section>

              <section className="mentions-section">
                <h3>4. Usage réservé et confidentialité</h3>
                <p>
                  L'accès à SecuFire Action est strictement limité aux
                  utilisateurs disposant d'un code d'accès personnel, attribué
                  dans le cadre d'une convention de partenariat dûment signée.
                  Tout usage en dehors de ce cadre est formellement interdit.
                </p>
                <div className="mentions-blockquote">
                  <p>
                    Les données, prédictions et exports produits par SecuFire
                    Action sont strictement confidentiels. Ils ne doivent pas
                    être communiqués, reproduits ou diffusés à des tiers non
                    habilités sans accord préalable écrit de l'EDHEC Climate
                    Institute et de Climate Innov SAS.
                  </p>
                </div>
              </section>

              <section className="mentions-section">
                <h3>5. Responsabilité</h3>
                <div className="mentions-blockquote">
                  <p>
                    L'EDHEC Climate Institute et Climate Innov SAS déclinent
                    toute responsabilité pour les décisions prises par les
                    utilisateurs sur la base des informations fournies par
                    SecuFire Action, ainsi que pour toutes conséquences directes
                    ou indirectes résultant de l'utilisation ou de la
                    non-utilisation de ces informations. L'utilisation de la
                    Plateforme et de ses résultats relève de la seule
                    responsabilité de l'utilisateur et de la structure qu'il
                    représente.
                  </p>
                </div>
              </section>

              <section className="mentions-section">
                <h3>6. Propriété intellectuelle</h3>
                <p>
                  L'ensemble des contenus, algorithmes, modèles et interfaces de
                  SecuFire Action sont protégés par le droit de la propriété
                  intellectuelle. Toute reproduction ou extraction non autorisée
                  est interdite et susceptible d'engager la responsabilité
                  civile et pénale de son auteur.
                </p>
              </section>

              <section className="mentions-section">
                <h3>7. Acceptation</h3>
                <div className="mentions-blockquote">
                  <p>
                    En accédant à la plateforme SecuFire Action, l'utilisateur
                    reconnaît avoir pris connaissance du présent avertissement
                    dans son intégralité, en avoir compris la portée, et
                    s'engage à respecter les conditions d'utilisation qui y sont
                    décrites. Cette acceptation est enregistrée et horodatée à
                    chaque ouverture de session.
                  </p>
                </div>
                {/* <div className="mentions-disclaimer-gate">
<label className="mentions-checkbox-label">
<input
                type="checkbox"
                className="mentions-checkbox-input"
                checked={isDisclaimerChecked}
                onChange={(e) => setIsDisclaimerChecked(e.target.checked)}
              />
<span>
                J'ai pris connaissance des CGU ci-dessus et je souhaite
                consulter l'avertissement d'usage
</span>
</label>
</div> */}

                <div className="mentions-disclaimer-gate">
                  <label className="mentions-checkbox-label">
                    <input
                      type="checkbox"
                      className="mentions-checkbox-input"
                      checked={isDisclaimerChecked}
                      onChange={(e) => setIsDisclaimerChecked(e.target.checked)}
                    />
                    <span>
                      J'ai lu et j'accepte les mentions légales, CGU et
                      avertissement
                    </span>
                  </label>
                </div>
              </section>

              <div className="mentions-footer-credit">
                <em>
                  Climate Innov en partenariat scientifique avec EDHEC Climate
                  Institute
                </em>
                <br />
                <a href="mailto:presse@climateimpactedhec.com">
                  presse@climateimpactedhec.com
                </a>
                {" · "}
                <a href="mailto:contact@climate-innov.com">
                  contact@climate-innov.com
                </a>
              </div>
            </>
          )}
        </div>

        {/* ── Footer / accept ── */}
        <div className="mentions-footer">
          {error && <p className="mentions-error">{error}</p>}
          <p className="mentions-notice">
            ⚠️ Vous devez lire et accepter les CGU & Avertissement ci-dessus
            pour accéder à la plateforme. Cette acceptation sera enregistrée et
            horodatée.
          </p>
          <button
            className="mentions-accept-btn"
            onClick={onAccept}
            // disabled={isLoading || !isDisclaimerChecked}
            disabled={isLoading || !(isCguChecked && isDisclaimerChecked)}
          >
            {isLoading ? (
              <span className="mentions-btn-loading">
                <span className="mentions-spinner" />
                Validation en cours...
              </span>
            ) : (
              "J'ai lu et j'accepte les mentions légales, CGU et avertissement"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalMentions;
